/**
 * Vault — Grupos y accesos (F4). Grupos por área (sincronizados del community
 * map, solo lectura acá) y grupos ad-hoc (proyecto / cliente) con miembros y roles.
 */
import { useState } from 'react';
import { Plus, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { usePagination } from '@/hooks/usePagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmptyState, LoaderBlock } from '@/components/internal/shared';
import { AreaBadge, useVaultLabels } from '@/components/vault/vault-ui';
import { NewGroupDialog } from '@/components/vault/NewGroupDialog';
import { GroupDetailCard } from '@/components/vault/GroupDetailCard';
import { OwnerlessPanel } from '@/components/vault/OwnerlessPanel';
import { errMsg, useVaultAccess } from '@/hooks/useVault';

export function GroupsPage() {
  const { t } = useTranslation();
  const L = useVaultLabels();
  const utils = trpc.useUtils();
  const access = useVaultAccess();
  const list = trpc.vault.listGroups.useQuery(undefined, { staleTime: 60_000 });
  const [selected, setSelected] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [confirmSync, setConfirmSync] = useState(false);

  // "Sincronizar con community map" reusa el sync de áreas del panel de permisos
  // (Lambda principal, solo admin): community map → grupos de Cognito → Vault.
  const sync = trpc.profile.applyAreaRoleSync.useMutation({
    onSuccess: (r) => {
      utils.vault.listGroups.invalidate();
      toast.success(t('vault.groups.synced', 'Sincronizado: +{{a}} / −{{r}}', { a: r.added, r: r.removed }));
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const area = list.data?.area ?? [];
  const adhoc = list.data?.adhoc ?? [];
  // Los grupos por área son pocos (uno por área); los ad-hoc crecen con proyectos y clientes.
  const { pageItems: pageAdhoc, footer: adhocFooter } = usePagination(adhoc, 25);

  if (list.isError) {
    // un error nunca se disfraza de "sin grupos" (política de roles y visibilidad)
    return (
      <div className="mx-auto max-w-7xl p-2 sm:p-4">
        <EmptyState title={t('vault.groups.load_error', 'No se pudieron cargar los grupos')} description={errMsg(list.error)} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-2 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl">{t('vault.groups.title', 'Grupos y accesos')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('vault.groups.subtitle', 'Los grupos se arman sobre el área del community map. Se sincronizan solos; podés crear grupos ad-hoc para proyectos o clientes.')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {access.data?.isAdmin && (
            <Button variant="outline" onClick={() => setConfirmSync(true)} disabled={sync.isPending}>
              <RefreshCw className={`mr-2 h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
              {t('vault.groups.sync', 'Sincronizar con community map')}
            </Button>
          )}
          <Button onClick={() => setNewOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('vault.groups.new', 'Nuevo grupo')}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
        <div className="space-y-4">
          {access.data?.isAdmin && <OwnerlessPanel />}
          <Card className="p-4 space-y-3" data-tour="groups-area">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">
                {t('vault.groups.area_title', 'Grupos por área')} <span className="font-normal text-muted-foreground">· {t('vault.groups.area_synced', 'sincronizados')}</span>
              </h2>
              <Badge variant="success">Auto</Badge>
            </div>
            {list.isLoading ? (
              <LoaderBlock />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('vault.groups.col_group', 'Grupo')}</TableHead>
                    <TableHead className="text-right">{t('vault.groups.col_members', 'Miembros')}</TableHead>
                    <TableHead className="text-right">{t('vault.groups.col_secrets', 'Credenciales')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {area.map((g) => (
                    <TableRow key={g.group}>
                      <TableCell><AreaBadge area={g.group} /></TableCell>
                      <TableCell className="text-right">{g.members ?? '—'}</TableCell>
                      <TableCell className="text-right">{g.secrets}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <p className="text-xs text-muted-foreground">
              {t('vault.groups.area_note', 'La membresía sale de la columna Área del community map (mismo sync que alimenta los grupos de Cognito). Alta y baja de personas se propagan solas.')}
            </p>
          </Card>

          <Card className="p-4 space-y-3" data-tour="groups-adhoc">
            <h2 className="font-semibold">{t('vault.groups.adhoc_title', 'Grupos ad-hoc')}</h2>
            {list.isLoading ? (
              <LoaderBlock />
            ) : adhoc.length === 0 ? (
              <EmptyState title={t('vault.groups.adhoc_empty', 'Todavía no hay grupos ad-hoc')} description={t('vault.groups.adhoc_empty_body', 'Creá uno para un proyecto o un cliente y compartile credenciales desde cada una.')} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('vault.groups.col_group', 'Grupo')}</TableHead>
                    <TableHead>{t('vault.groups.col_responsible', 'Responsable')}</TableHead>
                    <TableHead className="text-right">{t('vault.groups.col_members', 'Miembros')}</TableHead>
                    <TableHead className="text-right">{t('vault.groups.col_secrets', 'Credenciales')}</TableHead>
                    <TableHead>{t('vault.groups.col_my_role', 'Mi rol')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageAdhoc.map((g) => (
                    <TableRow key={g.groupId} className={`cursor-pointer ${selected === g.groupId ? 'bg-muted/50' : ''}`} onClick={() => setSelected(g.groupId)}>
                      <TableCell className="font-medium">{g.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{g.responsibleEmail}</TableCell>
                      <TableCell className="text-right">{g.memberCount}</TableCell>
                      <TableCell className="text-right">{g.secrets}</TableCell>
                      <TableCell>{g.myRole ? <Badge variant={g.myRole === 'manager' ? 'brand' : 'secondary'}>{L.role(g.myRole)}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {adhocFooter}
          </Card>
        </div>

        <div>
          {selected ? (
            <GroupDetailCard groupId={selected} onDeleted={() => setSelected(null)} />
          ) : (
            <Card className="p-6 text-center text-sm text-muted-foreground">{t('vault.groups.select_hint', 'Elegí un grupo ad-hoc para ver sus miembros y credenciales.')}</Card>
          )}
        </div>
      </div>

      <NewGroupDialog open={newOpen} onOpenChange={setNewOpen} onCreated={setSelected} />

      <AlertDialog open={confirmSync} onOpenChange={setConfirmSync}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vault.groups.sync_title', '¿Sincronizar los grupos de área ahora?')}</AlertDialogTitle>
            <AlertDialogDescription>{t('vault.groups.sync_body', 'Aplica el diff community map → grupos de Cognito (lo mismo que corre a diario). Quien salga de un grupo deja marcadas para rotación las credenciales que ese grupo alcanza.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => sync.mutate()}>{t('vault.groups.sync', 'Sincronizar con community map')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
